import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { type ChangeEvent, useCallback, useMemo, useState } from 'react';
import { z } from 'zod';
import Icon from '@/components/Icon/Icon';
import { VStack } from '@/components/Layout';
import { Text } from '@/components/Typography';
import { type MetricRowKey, useMetricRowVisibility } from '@/features/contract-and-cost/grid';
import { PaymentOverviewFilterBar, type PaymentGroupBy, type PaymentOverviewViewMode, type ComparisonTarget } from '@/features/contract-and-cost/payment';
import { getMonthsInPeriod, parsePeriodStart } from '@/features/contract-and-cost/period';
import { PaymentStatCards, PaymentSummaryChart } from '@/features/software/contract-and-cost/components';
import { cn } from '@/lib/cn';
import { featureFlags } from '@/lib/env';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { DepartmentPaymentTable } from './-components/DepartmentPaymentTable';
import { MemberPaymentTable } from './-components/MemberPaymentTable';
import { MonthlyPaymentTable } from './-components/MonthlyPaymentTable';
import { PaymentOverviewTable } from './-components/PaymentOverviewTable';
import { useSoftwareMonthlyPaymentList, useSoftwarePaymentByDepartment, useSoftwarePaymentByMember, useSoftwarePaymentOverviewList, useSoftwarePaymentOverviewSummary } from './-query';
const SearchSchema = z.object({
  groupBy: z.enum(['catalog', 'member', 'department']).default('catalog'),
  viewMode: z.enum(['monthly', 'transition']).default('transition'),
  month: z.number().min(1).max(12).optional(),
  freeText: z.string().optional(),
  comparisonTarget: z.enum(['PREVIOUS_YEAR', 'PREVIOUS_MONTH']).default('PREVIOUS_MONTH'),
  pageIndex: z.number().default(0),
  pageSize: z.number().default(100)
});
export const Route = createFileRoute('/_staff/software/_layout/contract-and-cost/_contract-and-cost/payment/$period/')({
  component: PaymentOverviewPage,
  validateSearch: zodValidator(SearchSchema)
});
function PaymentOverviewPage() {
  const {
    period
  } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'features.contractAndCost.paymentOverview'
  });

  // CONTRACT_AND_COST_NEW_VIEW が無効な場合はカタログ別のみ提供する。
  // 個人別/組織別は非表示にし、URL 直アクセス時も catalog に強制する。
  const isNewViewEnabled = featureFlags.check('CONTRACT_AND_COST_NEW_VIEW');
  const groupBy = isNewViewEnabled ? search.groupBy : 'catalog';
  const groupByOptions: readonly PaymentGroupBy[] = isNewViewEnabled ? ['catalog', 'member', 'department'] : ['catalog'];
  const isCatalog = groupBy === 'catalog';
  const isMember = groupBy === 'member';
  const isDepartment = groupBy === 'department';
  const isMonthly = isCatalog && search.viewMode === 'monthly';
  const monthOptions = useMemo(() => getMonthsInPeriod(period), [period]);
  // マウント時の月を固定（毎レンダーで new Date() を生成しないため）。
  const fallbackMonth = useMemo(() => new Date().getMonth() + 1, []);
  const currentMonth = search.month ?? fallbackMonth;

  // 行表示トグル（実績 / 予定 / 予実差異）。親に保持するため groupBy 切替時も維持される。
  const {
    visibility: rowVisibility,
    toggleRow: handleRowVisibilityToggle
  } = useMetricRowVisibility();
  // トグルの表示名はテーブルの行ラベル（rowType）と一致させる。
  const {
    t: tRowType
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.software.contractAndCost.paymentOverview.table.rowType'
  });
  const rowToggleItems: ReadonlyArray<{
    key: MetricRowKey;
    label: string;
  }> = [{
    key: 'actual',
    label: tRowType('actual')
  }, {
    key: 'scheduled',
    label: tRowType('scheduled')
  }, {
    key: 'variance',
    label: tRowType('variance')
  }];
  const [showSummary, setShowSummary] = useState(false);
  const summaryRegionId = 'software-layout-payment-overview-summary-region';
  const [pagination, setPagination] = useState({
    page: search.pageIndex + 1,
    pageSize: search.pageSize
  });

  // period から派生する固定値。毎レンダーで Date を生成しないよう period 変化時のみ再計算する。
  const targetYearMonth = useMemo(() => {
    const parsedPeriodStart = parsePeriodStart(period);
    return new Date(Date.UTC(parsedPeriodStart.year, parsedPeriodStart.month - 1, 1)).toISOString();
  }, [period]);
  const {
    data: summary
  } = useSoftwarePaymentOverviewSummary({
    targetYearMonth
  });
  const transitionQuery = useSoftwarePaymentOverviewList({
    period,
    targetYearMonth,
    paymentComparisonTarget: search.comparisonTarget,
    page: pagination.page,
    pageSize: pagination.pageSize,
    freeText: search.freeText
  }, isCatalog && !isMonthly);
  const monthlyQuery = useSoftwareMonthlyPaymentList({
    period,
    viewMode: 'monthly',
    month: currentMonth,
    page: pagination.page,
    pageSize: pagination.pageSize,
    freeText: search.freeText
  }, isMonthly);
  const memberQuery = useSoftwarePaymentByMember({
    startYearMonth: targetYearMonth,
    paymentComparisonTarget: search.comparisonTarget,
    durationMonths: monthOptions.length,
    page: pagination.page,
    pageSize: pagination.pageSize,
    freeText: search.freeText
  }, isMember);
  const departmentDrilldown = useSoftwarePaymentByDepartment({
    startYearMonth: targetYearMonth,
    paymentComparisonTarget: search.comparisonTarget,
    durationMonths: monthOptions.length
  }, {
    page: pagination.page,
    pageSize: pagination.pageSize
  }, isDepartment);
  const handlePageChange = useCallback((page: number) => {
    setPagination(prev => ({
      ...prev,
      page
    }));
    navigate({
      search: prev => ({
        ...prev,
        pageIndex: page - 1
      }),
      replace: true
    });
  }, [navigate]);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({
      page: 1,
      pageSize
    });
    navigate({
      search: prev => ({
        ...prev,
        pageIndex: 0,
        pageSize
      }),
      replace: true
    });
  }, [navigate]);
  const handleGroupByChange = useCallback((next: PaymentGroupBy) => {
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
    navigate({
      search: prev => ({
        ...prev,
        groupBy: next,
        // 個人別/組織別は推移のみ対応のため月選択をクリアする
        viewMode: next === 'catalog' ? prev.viewMode : 'transition',
        month: undefined,
        pageIndex: 0
      }),
      replace: true
    });
  }, [navigate]);
  const handleViewModeChange = useCallback((mode: PaymentOverviewViewMode) => {
    setPagination({
      page: 1,
      pageSize: pagination.pageSize
    });
    navigate({
      search: prev => ({
        ...prev,
        viewMode: mode,
        month: mode === 'monthly' ? currentMonth : undefined,
        pageIndex: 0
      }),
      replace: true
    });
  }, [navigate, currentMonth, pagination.pageSize]);
  const handleMonthChange = useCallback((month: number) => {
    navigate({
      search: prev => ({
        ...prev,
        month
      }),
      replace: true
    });
  }, [navigate]);
  const handleComparisonTargetChange = useCallback((target: ComparisonTarget) => {
    navigate({
      search: prev => ({
        ...prev,
        comparisonTarget: target
      }),
      replace: true
    });
  }, [navigate]);
  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
    navigate({
      search: prev => ({
        ...prev,
        freeText: e.target.value || undefined,
        pageIndex: 0
      }),
      replace: true
    });
  }, [navigate]);

  // 組織別はフルテキスト検索の入力を持たない
  const showSearch = !isDepartment;
  return <div className="relative flex flex-1 flex-col gap-5 overflow-auto px-12 py-5 min-h-0">
      <div id={summaryRegionId} hidden={!showSummary}>
        {summary && <VStack className="gap-5">
            <PaymentStatCards summary={summary} />
            <PaymentSummaryChart data={summary.months} months={monthOptions} />
          </VStack>}
      </div>

      <div className="flex justify-end">
        <button type="button" className="flex items-center gap-1 transition-colors" aria-expanded={showSummary} aria-controls={summaryRegionId} onClick={() => setShowSummary(prev => !prev)}>
          <Icon iconName={showSummary ? 'ChevronUpIcon' : 'ChevronDownIcon'} className="size-3.5" />
          <Text className={cn('text-labelSmallReg', 'text-neutral-muted-on-fill', 'hover:text-neutral-on-fill')}>
            {showSummary ? t('summary.collapse') : t('summary.expand')}
          </Text>
        </button>
      </div>

      <PaymentOverviewFilterBar groupBy={groupBy} onGroupByChange={handleGroupByChange} groupByOptions={groupByOptions} viewMode={search.viewMode} onViewModeChange={handleViewModeChange} selectedMonth={isMonthly ? currentMonth : undefined} onSelectedMonthChange={handleMonthChange} monthOptions={monthOptions} searchValue={showSearch ? search.freeText ?? '' : undefined} onSearchChange={showSearch ? handleSearchChange : undefined} comparisonTarget={search.comparisonTarget} onComparisonTargetChange={handleComparisonTargetChange} rowToggle={isMonthly ? undefined : {
      items: rowToggleItems,
      visibility: rowVisibility,
      onToggle: handleRowVisibilityToggle
    }} />

      {isMember && <MemberPaymentTable data={memberQuery.data} isPending={memberQuery.isPending} isError={memberQuery.isError} monthList={monthOptions} currentPage={pagination.page} pageSize={pagination.pageSize} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} rowVisibility={rowVisibility} className="flex-1 min-h-0" />}

      {isDepartment && <DepartmentPaymentTable rootData={departmentDrilldown.rootData} visibleRows={departmentDrilldown.visibleRows} onToggle={departmentDrilldown.toggle} isPending={departmentDrilldown.isPending} isError={departmentDrilldown.isError} monthList={monthOptions} currentPage={pagination.page} pageSize={pagination.pageSize} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} rowVisibility={rowVisibility} className="flex-1 min-h-0" />}

      {isCatalog && (isMonthly ? <MonthlyPaymentTable data={monthlyQuery.data?.items ?? []} isPending={monthlyQuery.isPending} totalCount={monthlyQuery.data?.count} pageCount={monthlyQuery.data?.maxPage} pagination={{
      pageIndex: pagination.page - 1,
      pageSize: pagination.pageSize
    }} onPaginationChange={next => {
      setPagination({
        page: next.pageIndex + 1,
        pageSize: next.pageSize
      });
      navigate({
        search: prev => ({
          ...prev,
          pageIndex: next.pageIndex,
          pageSize: next.pageSize
        }),
        replace: true
      });
    }} className="flex-1 min-h-0" /> : <PaymentOverviewTable data={transitionQuery.data?.items ?? []} isPending={transitionQuery.isPending} months={monthOptions} totalCount={transitionQuery.data?.count} pageCount={transitionQuery.data?.maxPage} currentPage={pagination.page} pageSize={pagination.pageSize} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} rowVisibility={rowVisibility} className="flex-1 min-h-0" />)}
    </div>;
}